/* eslint-disable lines-between-class-members */
import * as THREE from "three";
import { logicalChildrenOf, ScriptableMixin, ScriptableObject, ScriptableObjects } from "./ScriptableObjects.js";
import { sanitizeUrl } from "@braintree/sanitize-url";

/* ────────────────────────────────────────────────────────────────────────── */
/* Environment & helpers                                                      */
/* ────────────────────────────────────────────────────────────────────────── */
const HAS_AFRAME = typeof window !== "undefined" && !!window.AFRAME;
// eslint-disable-next-line no-undef
const AudioCtor = (typeof globalThis !== "undefined" && (globalThis.AudioNode || globalThis.Audio)) || undefined;

const isAFrameEntity = v => !!(v && typeof v.addEventListener === "function" && v.object3D);
const isObject3D = v => !!(v && (v.isObject3D || (typeof THREE !== "undefined" && v instanceof THREE.Object3D)));

/* ────────────────────────────────────────────────────────────────────────── */
/* Public API: properties                                                     */
/* ────────────────────────────────────────────────────────────────────────── */
const AUDIO_PROPS = [
  "src",
  "controls",
  "controlsVolume",
  "controlsPlayPause",
  "controlsSeek",
  "controlsTime",
  "autoPlay",
  "loop",
  "enableOnProximity",
  "proximityRadius",
  "audioOnlyInsideScene",
  "audioPreventTwice",
  "linkedModel",
  "disposeAnimation",
  "networkable"
];

/* ────────────────────────────────────────────────────────────────────────── */
/* A-Frame adapter                                                            */
/* ────────────────────────────────────────────────────────────────────────── */

class AudioAFrameAdapter {
  constructor(sourceObject) {
    this.sourceObject = sourceObject;

    const MEDIA_LOADER_COMPONENT = "media-loader";
    const LINK_SEQ_COMPONENT = "link-sequence";

    // Helpers to work with media-loader -----------------------------------
    // We may be invoked before the media-loader component data is available.
    // In that scenario, performing a blind write would overwrite its
    // configuration. Cache updates in `_pendingUpdates` and merge them once
    // the component is initialised.

    const _pendingUpdates = {};

    const _flushPending = () => {
      if (!this.sourceObject?.el) return;
      const current = {
        ...(this.sourceObject.el.getAttribute(MEDIA_LOADER_COMPONENT) || {})
      };

      // top-level fields
      Object.keys(_pendingUpdates).forEach(k => {
        if (k === "mediaOptions") return;
        current[k] = _pendingUpdates[k];
      });

      if (_pendingUpdates.mediaOptions) {
        current.mediaOptions = {
          ...(current.mediaOptions || {}),
          ..._pendingUpdates.mediaOptions
        };
      }

      Object.keys(_pendingUpdates).forEach(k => delete _pendingUpdates[k]);
      this.sourceObject.el.setAttribute(MEDIA_LOADER_COMPONENT, current);
    };

    if (this.sourceObject?.el) {
      this.sourceObject.el.addEventListener("componentinitialized", e => {
        if (e?.detail?.name === MEDIA_LOADER_COMPONENT) {
          _flushPending();
        }
      });
    }

    const setMediaLoaderProp = (key, val, inMediaOptions = false) => {
      if (!this.sourceObject?.el) return;

      const attr = this.sourceObject.el.getAttribute(MEDIA_LOADER_COMPONENT);

      if (!attr) {
        if (inMediaOptions) {
          _pendingUpdates.mediaOptions = {
            ...(_pendingUpdates.mediaOptions || {}),
            [key]: val
          };
        } else {
          _pendingUpdates[key] = val;
        }
        return;
      }

      const current = { ...attr };
      if (inMediaOptions) {
        current.mediaOptions = { ...(current.mediaOptions || {}), [key]: val };
      } else {
        current[key] = val;
      }
      this.sourceObject.el.setAttribute(MEDIA_LOADER_COMPONENT, current);
    };

    const getMediaLoaderProp = (key, inMediaOptions = false) => {
      const attr = this.sourceObject?.el?.getAttribute(MEDIA_LOADER_COMPONENT);
      if (attr) {
        return inMediaOptions ? attr.mediaOptions?.[key] : attr[key];
      }

      // Fall back to pending updates when component isn't initialized
      if (inMediaOptions) {
        return _pendingUpdates.mediaOptions?.[key];
      } else {
        return _pendingUpdates[key];
      }
    };

    // Helper for video-pause-state sync -----------------------------------
    const updateVideoPauseState = () => {
      const autoPlay = getMediaLoaderProp("autoPlay", true);
      const audioOnlyInsideScene = getMediaLoaderProp(
        "audioOnlyInsideScene",
        true
      );
      const paused = !autoPlay || audioOnlyInsideScene;
      this.sourceObject?.el?.setAttribute("video-pause-state", { paused });
    };

    // Audio properties mapped to media-loader -----------------------------
    Object.defineProperty(this, "src", {
      get: () => getMediaLoaderProp("src"),
      set: v => {
        setMediaLoaderProp("src", v);
      }
    });

    ["loop", "audioOnlyInsideScene", "audioPreventTwice"].forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => getMediaLoaderProp(prop, true),
        set: v => {
          setMediaLoaderProp(prop, v, true);
          if (prop === "audioOnlyInsideScene") updateVideoPauseState();
        }
      });
    });

    Object.defineProperty(this, "autoPlay", {
      get: () => getMediaLoaderProp("autoPlay", true),
      set: v => {
        setMediaLoaderProp("autoPlay", v, true);
        updateVideoPauseState();
      }
    });

    Object.defineProperty(this, "controls", {
      get: () => !getMediaLoaderProp("hidePlaybackControls", true),
      set: v => {
        setMediaLoaderProp("hidePlaybackControls", !v, true);
      }
    });

    // granular controls properties -------------------------------------
    Object.defineProperty(this, "controlsVolume", {
      get: () => getMediaLoaderProp("controlsVolume", true),
      set: v => {
        setMediaLoaderProp("controlsVolume", v, true);
      }
    });

    Object.defineProperty(this, "controlsPlayPause", {
      get: () => getMediaLoaderProp("controlsPlayPause", true),
      set: v => {
        setMediaLoaderProp("controlsPlayPause", v, true);
      }
    });

    Object.defineProperty(this, "controlsSeek", {
      get: () => getMediaLoaderProp("controlsSeek", true),
      set: v => {
        setMediaLoaderProp("controlsSeek", v, true);
      }
    });

    Object.defineProperty(this, "controlsTime", {
      get: () => getMediaLoaderProp("controlsTime", true),
      set: v => {
        setMediaLoaderProp("controlsTime", v, true);
      }
    });

    // ------------------------------------------------------------------
    // Proximity helpers & networked updates
    // ------------------------------------------------------------------

    const getEnableOnProximityAttr = () =>
      this.sourceObject?.el?.getAttribute("enable-on-proximity");

    const updateNetworkedComponent = () => {
      if (!this.sourceObject?.el) return;
      const el = this.sourceObject.el;

      const enableProximity = el.hasAttribute("enable-on-proximity");

      if (!enableProximity) {
        if (!el.hasAttribute("networked")) {
          // If Controls is true
          const isControlled =
            this.sourceObject?.el?.getAttribute("controls") ?? this.controls;

          el.setAttribute("networked", {
            template: isControlled
              ? "#static-controlled-media"
              : "#static-media",
            owner: "scene",
            persistent: true,
          });
        }
      } else if (el.hasAttribute("networked")) {
        el.removeAttribute("networked");
      }
    };

    // enableOnProximity --------------------------------------------------
    Object.defineProperty(this, "enableOnProximity", {
      get: () =>
        this.sourceObject?.el?.hasAttribute("enable-on-proximity") || false,
      set: v => {
        if (!this.sourceObject?.el) return;

        if (v) {
          const current = getEnableOnProximityAttr() || {};
          const distance = current.distance ?? this.proximityRadius ?? 1;
          this.sourceObject.el.setAttribute("enable-on-proximity", {
            distance
          });
        } else {
          this.sourceObject.el.removeAttribute("enable-on-proximity");
        }

        updateNetworkedComponent();
      }
    });

    // proximityRadius ----------------------------------------------------
    Object.defineProperty(this, "proximityRadius", {
      get: () => getEnableOnProximityAttr()?.distance,
      set: v => {
        if (!this.sourceObject?.el) return;
        if (this.sourceObject.el.hasAttribute("enable-on-proximity")) {
          const current = getEnableOnProximityAttr() || {};
          this.sourceObject.el.setAttribute("enable-on-proximity", {
            ...current,
            distance: v
          });
        }
      }
    });

    // networkable boolean --------------------------------------------------

    Object.defineProperty(this, "networkable", {
      get: () => this.sourceObject?.el?.hasAttribute("networked"),
      set: v => {
        if (!this.sourceObject?.el) return;

        if (v) {
          if (!this.sourceObject.el.hasAttribute("networked")) {

            const isControlled =
              this.sourceObject?.el?.getAttribute("controls") ?? this.controls;

            this.sourceObject.el.setAttribute("networked", {
              template: isControlled
                ? "#static-controlled-media"
                : "#static-media",
            });
          }
        } else {
          this.sourceObject.el.removeAttribute("networked");
        }
      }
    });

    // link-sequence props ---------------------------------------------------
    const setLinkSeqProp = (key, val) => {
      if (!this.sourceObject?.el) return;
      const current = {
        ...(this.sourceObject.el.getAttribute(LINK_SEQ_COMPONENT) || {})
      };
      current[key] = val;
      this.sourceObject.el.setAttribute(LINK_SEQ_COMPONENT, current);
    };

    const getLinkSeqProp = key =>
      this.sourceObject?.el?.getAttribute(LINK_SEQ_COMPONENT)?.[key];

    ["linkedModel", "disposeAnimation"].forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => getLinkSeqProp(prop),
        set: v => {
          setLinkSeqProp(prop, v);
        }
      });
    });

    Object.defineProperty(this, "paused", {
      get: () => {
        if (!this.sourceObject?.el) return true;
        const loader = this.sourceObject.el.components?.["media-loader"];
        return loader?.videoPaused ?? true;
      }
    });

    this.play = () => {
      const el = this.sourceObject?.el;
      if (!el) return;
      const mv = el.components["media-video"];
      if (mv) mv.playVideo?.();
      else setMediaLoaderProp("videoPaused", false, true);
    };

    this.pause = () => {
      const el = this.sourceObject?.el;
      if (!el) return;
      const mv = el.components["media-video"];
      if (mv) {
        if (mv.pauseVideo) mv.pauseVideo();
        else el.setAttribute("media-video", "videoPaused", true);
      } else {
        setMediaLoaderProp("videoPaused", true, true);
      }
    };

    this.stop = () => {
      const el = this.sourceObject?.el;
      if (!el) return;
      const mv = el.components["media-video"];
      if (mv) {
        if (mv.stopVideo) mv.stopVideo();
        else mv.stop?.();
      } else {
        setMediaLoaderProp("videoPaused", true, true);
        setMediaLoaderProp("time", 0);
      }
    };
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Editor / plain-Three adapter                                               */
/* ────────────────────────────────────────────────────────────────────────── */
class AudioEditorAdapter {
  constructor(sourceObject, originalDescriptors = new Map()) {
    this.sourceObject = sourceObject;
    this._values = new Map();
    this._originalDescriptors = originalDescriptors;

    AUDIO_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => this._values.get(prop),
        set: v => {
          this._values.set(prop, v);
          const desc = this._originalDescriptors.get(prop);
          if (desc?.set) {
            try {
              desc.set.call(this.sourceObject, v);
            } catch {
              /* ignore */
            }
          } else {
            try {
              this.sourceObject[prop] = v;
            } catch {
              /* ignore */
            }
          }
        }
      });

      // seed from source without invoking setters when possible
      const desc = originalDescriptors.get(prop);
      let initial;
      try {
        if (desc?.get) initial = desc.get.call(sourceObject);
        else if (Object.prototype.hasOwnProperty.call(sourceObject, prop)) initial = sourceObject[prop];
      } catch {
        /* ignore */
      }
      
      this._values.set(prop, initial);
    });

    Object.defineProperty(this, "paused", {
      get: () => {
        const d = this._originalDescriptors.get("paused");
        if (d?.get) return d.get.call(this.sourceObject);
        return this.sourceObject?.paused ?? true;
      }
    });
  }

  dispose() {
    this._values.clear();
    this.sourceObject = null;
  }

  play() {
    const d = this._originalDescriptors.get("play");
    if (d?.value) d.value.call(this.sourceObject);
    else if (this.sourceObject?.onPlay) this.sourceObject.onPlay();
  }

  pause() {
    const d = this._originalDescriptors.get("pause");
    if (d?.value) d.value.call(this.sourceObject);
    else if (this.sourceObject?.onPause) this.sourceObject.onPause();
  }

  stop() {
    const d = this._originalDescriptors.get("stop");
    if (d?.value) {
      d.value.call(this.sourceObject);
    } else if (this.sourceObject?.stop) {
      this.sourceObject.stop();
    } else {
      this.pause();
      if (this.sourceObject) this.sourceObject.currentTime = 0;
    }
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Lazy adapter bootstrap (works for adopted instances)                       */
/* ────────────────────────────────────────────────────────────────────────── */
function collectDescriptorsFor(obj) {
  const map = new Map();
  const interested = [...AUDIO_PROPS, "play", "pause", "paused", "stop"];
  for (const prop of interested) {
    let cur = obj;
    while (cur) {
      const d = Object.getOwnPropertyDescriptor(cur, prop);
      if (d) {
        map.set(prop, d);
        break;
      }
      cur = Object.getPrototypeOf(cur);
    }
    if (!map.has(prop)) map.set(prop, undefined);
  }
  return map;
}

function ensureAdapter(self) {
  if (self._adapter) return self._adapter;

  // A-Frame path if we have an element
  if (HAS_AFRAME && (self.el || self.__el)) {
    Object.defineProperty(self, "_adapter", { value: new AudioAFrameAdapter(self), configurable: true });
    return self._adapter;
  }

  // Editor / plain Three path
  const descriptors = collectDescriptorsFor(self);
  Object.defineProperty(self, "_adapter", {
    value: new AudioEditorAdapter(self, descriptors),
    configurable: true
  });
  return self._adapter;
}

export class AudioElement extends ScriptableObject {
  static scriptableType = "audio";

  constructor(initial = {}) {
    super();

    // A-Frame creation: build <a-entity>, adopt, and return the shifted object
    if (HAS_AFRAME) {
      const el = document.createElement("a-entity");


      // Determine whether the audio should use the "controlled" media template
      const isControlled = initial.controls !== false && !initial.enableOnProximity;
      if (initial.networkable !== false && !initial.enableOnProximity) {
        el.setAttribute("networked", {
          template: isControlled ? "#static-controlled-media" : "#static-media",
        });
      }

      const initialAutoPlay = initial.autoPlay ?? true;

      const mediaOptions = {
        autoPlay: initialAutoPlay,
        loop: initial.loop,
        audioOnlyInsideScene: initial.audioOnlyInsideScene,
        preventPlayTwice: initial.audioPreventTwice,
        hidePlaybackControls: !isControlled,
        controlsVolume: initial.controlsVolume !== undefined ? initial.controlsVolume : isControlled,
        controlsPlayPause: initial.controlsPlayPause !== undefined ? initial.controlsPlayPause : isControlled,
        controlsSeek: initial.controlsSeek !== undefined ? initial.controlsSeek : isControlled,
        controlsTime: initial.controlsTime !== undefined ? initial.controlsTime : isControlled,
        videoPaused: !initialAutoPlay || initial.audioOnlyInsideScene
      };

      // Determine videoPaused value analogous to inflator logic
      const videoPaused = !initialAutoPlay || initial.audioOnlyInsideScene;
      el.setAttribute("video-pause-state", { paused: videoPaused });

      // Apply media-loader component
      el.setAttribute("media-loader", {
        src: sanitizeUrl(initial.src || ""),
        fitToBox: false,
        resolve: true,
        fileIsOwned: true,
        animate: false,
        mediaOptions,
        moveTheParentNotTheMesh: true,
        useFancyLoader: false
      });

      if (initial.enableOnProximity) {
        el.setAttribute("enable-on-proximity", {
          distance: initial.proximityRadius
        });
      }

      // Apply link-sequence component if needed
      if (initial.linkedModel || initial.disposeAnimation) {
        el.setAttribute("link-sequence", {
          linkedModel: initial.linkedModel,
          disposeAnimation: initial.disposeAnimation
        });
      }

      const obj = this.constructor.adopt(el);
      if (Object.getPrototypeOf(obj) !== this.constructor.prototype) {
        Object.setPrototypeOf(obj, this.constructor.prototype);
      }

      // lazy init adapter and apply initial props
      ensureAdapter(obj);
      for (const k of Object.keys(initial)) {
        if (k in obj) obj[k] = initial[k];
      }

      return obj; // IMPORTANT: return shifted object
    }

    // Editor / plain Three: construct real AudioNode and upgrade to scriptable
    const node = createAudioElementEditor(initial);
    return node;
  }

  copy(source, recursive = true) {
    if (!source || !source.isScriptableObject) return this;

    // 1) copy transform only (no children)
    THREE.Object3D.prototype.copy.call(this, source, /* recursive = */ false);

    // 2) copy public props via adapter (deep where needed)
    const take = (prop, val) => {
      if (val === undefined) return;
      
      // deep-clone where necessary to avoid shared refs
      if (Array.isArray(val)) {
        this[prop] = val.slice();
      } else {
        this[prop] = val;
      }
    };

    for (const p of AUDIO_PROPS) {
      take(p, source[p]);
    }

    // 3) clear existing children
    if (this.children?.length) {
      for (const c of [...this.children]) THREE.Object3D.prototype.remove.call(this, c);
    }

    // 4) replicate children via our public API
    if (recursive) {
      for (const child of logicalChildrenOf(source)) {
        this.add(child.clone());
      }
    }

    return this;
  }

  clone(recursive = true) {
    const init = {};

    for (const p of AUDIO_PROPS) {
      const v = this[p];
      if (v === undefined) continue;
      
      // Follow the same pattern as other implementations
      if (Array.isArray(v)) {
        init[p] = v.slice();
      } else {
        init[p] = v;
      }
    }

    const dest = new AudioElement(init);

    // Copy transforms/children; copy() also uses the adapter surface.
    dest.position.copy(this.position);
    dest.quaternion.copy(this.quaternion);
    dest.scale.copy(this.scale);

    return dest.copy(this, recursive);
  }

  // convenience (optional)
  static create(initial = {}) {
    return new this(initial);
  }

  play() {
    return ensureAdapter(this)?.play();
  }

  pause() {
    return ensureAdapter(this)?.pause();
  }

  stop() {
    return ensureAdapter(this)?.stop();
  }

  get paused() {
    return ensureAdapter(this)?.paused ?? true;
  }

  get playing() {
    return !this.paused;
  }
}

// DRY helper: upgrade an editor AudioNode to scriptable and attach adapter + proxies
function makeEditorAudioNodeScriptable(node) {
  if (!node) return node;
  if (node.isScriptableObject === true) return node;

  // Preserve original chain and add Scriptable API on top
  const originalProto = Object.getPrototypeOf(node);
  const ScriptableClass = ScriptableMixin((originalProto && originalProto.constructor) || AudioCtor || THREE.Object3D);
  if (Object.getPrototypeOf(ScriptableClass.prototype) !== originalProto) {
    Object.setPrototypeOf(ScriptableClass.prototype, originalProto);
  }
  Object.setPrototypeOf(node, ScriptableClass.prototype);

  // Annotate type
  try {
    if (!node.scriptableType) 
      Object.defineProperty(node, "scriptableType", { value: AudioElement.scriptableType, configurable: true });
  } catch (_) {
    node.scriptableType = AudioElement.scriptableType;
  }

  const descriptors = collectDescriptorsFor(node);
  try {
    Object.defineProperty(node, "_adapter", { value: new AudioEditorAdapter(node, descriptors), configurable: true });
  } catch (_) {
    node._adapter = new AudioEditorAdapter(node, descriptors);
  }

  for (const prop of AUDIO_PROPS) {
    try {
      Object.defineProperty(node, prop, {
        get() {
          return this._adapter?.[prop];
        },
        set(v) {
          if (this._adapter) this._adapter[prop] = v;
        },
        enumerable: true,
        configurable: true
      });
    } catch (_) {
      /* best-effort */
    }
  }

  // Ensure AudioElement's enhanced `clone` logic is used for
  // editor-created audio nodes. Without this, the instance would fall back to
  // AudioNode defaults which do not propagate the
  // scriptable properties managed by the adapter.
  try {
    if (node.clone !== AudioElement.prototype.clone) {
      Object.defineProperty(node, "clone", {
        value: AudioElement.prototype.clone,
        writable: true,
        configurable: true
      });
    }

    ["play", "pause", "stop"].forEach(m => {
      Object.defineProperty(node, m, {
        value: AudioElement.prototype[m],
        writable: true,
        configurable: true
      });
    });

    ["paused", "playing"].forEach(p => {
      const d = Object.getOwnPropertyDescriptor(AudioElement.prototype, p);
      if (d) Object.defineProperty(node, p, d);
    });
  } catch (e) {
    console.error("AudioElement: failed to patch", e);
  }

  return node;
}

// Editor factory for Audio (editor/plain Three)
function createAudioElementEditor(initial = {}) {
  const Ctor = globalThis.AudioNode || globalThis.Audio;
  if (typeof Ctor !== "function") {
    throw new Error("AudioElementEditor: AudioNode constructor not found.");
  }

  let node;
  try {
    const editor = ScriptableObject.editor || globalThis.editor;
    node = new Ctor(editor);
  } catch (e) {
    throw new Error(`AudioElementEditor: failed to construct – ${e?.message || e}`);
  }

  makeEditorAudioNodeScriptable(node);

  for (const k of Object.keys(initial)) {
    if (k in node) {
      node[k] = initial[k];
    }
  }
  
  return node;
}

// If you create objects via adopt(el) (constructor doesn't run),
// the accessor getter calls ensureAdapter(this) and mounts the adapter on first access.
// That's why binding these getters on the prototype is crucial—even for adopted instances.
AUDIO_PROPS.forEach(prop => {
  Object.defineProperty(AudioElement.prototype, prop, {
    get() {
      return ensureAdapter(this)?.[prop];
    },
    set(v) {
      const a = ensureAdapter(this);
      if (a) a[prop] = v;
    },
    enumerable: true,
    configurable: true
  });
});

ScriptableObjects.registerType("audio", source => {
  if (!source) return source;

  // Only promote if type matches (brand can live on obj or on el.object3D)
  const type = source.scriptableType || source.object3D?.scriptableType || source?.__el?.object3D?.scriptableType || source?.el?.object3D?.scriptableType;
  if (type !== AudioElement.scriptableType) return source;

  // If given an A-Frame element or a Scriptable with .el -> adopt that element
  if (isAFrameEntity(source)) return AudioElement.adopt(source);

  // Editor/plain-Three: upgrade in-place to scriptable and attach adapter + proxies
  if (isObject3D(source)) return makeEditorAudioNodeScriptable(source);

  return source;
});

// Named exports expected by external consumers -----------------------------
export { AudioAFrameAdapter, AudioEditorAdapter, AUDIO_PROPS };
